/**
 * Splitting an oversized CSV export in the browser.
 *
 * ## Why this happens client-side
 *
 * A file too big to upload cannot be uploaded in order to be split. It also bounds what
 * the server holds per request no matter how large the whole export is.
 *
 * ## What moving to CSV removed
 *
 * The workbook version of this loaded SheetJS — ~400KB gzipped — to read a ZIP of XML,
 * rebuild a sheet model and write new workbooks. A CSV needs none of that: it is text,
 * so a split is a header block plus a slice of lines. No dependency, no dynamic import,
 * no code-split chunk, and nothing that can behave differently in one browser.
 *
 * ⚠️ It is still not `text.split('\n')`. A quoted field may legitimately contain a
 * newline — an address or a note routinely does — and splitting on raw newlines would
 * cut a record in half, giving one part a trailing fragment and the next a leading one.
 * Both then fail to parse, and the failure appears as corrupt data rather than as a bad
 * split. So lines are counted with the quoting rules applied.
 */

/** Data rows per part. Well under the server's cap, with room for wide rows. */
export const DEFAULT_ROWS_PER_PART = 20000;

/**
 * @param file        the oversized .csv
 * @param rowsPerPart data rows per part, excluding the header block
 * @returns {Promise<Array<{name: string, blob: Blob, rows: number}>>} parts, in order
 */
export async function splitCsv(file, rowsPerPart = DEFAULT_ROWS_PER_PART) {
    const text = stripBom(await file.text());
    const records = splitRecords(text);

    if (records.length === 0) {
        throw new Error('This file has no rows to split.');
    }

    // 🔑 Machanicdesk exports open with a title line ("Table 1") ABOVE the headers, and
    // every part must carry both. A part without its header is unparseable, and the
    // server reports that as "missing column" — a message pointing at the mapping
    // rather than at the split that caused it.
    const headerDepth = findHeaderDepth(records);
    const preamble = records.slice(0, headerDepth + 1);
    const body = records.slice(headerDepth + 1).filter((r) => r.trim() !== '');

    const partCount = Math.max(1, Math.ceil(body.length / rowsPerPart));
    const base = file.name.replace(/\.csv$/i, '');

    const parts = [];
    for (let i = 0; i < partCount; i++) {
        const slice = body.slice(i * rowsPerPart, (i + 1) * rowsPerPart);
        const content = [...preamble, ...slice].join('\n') + '\n';
        parts.push({
            name: `${base}-part${i + 1}of${partCount}.csv`,
            // ⚠️ The BOM is deliberately NOT written back. Excel adds one when saving
            // UTF-8, the server strips it, and re-adding it per part would be three
            // invisible bytes in front of the first header of every one of them.
            blob: new Blob([content], { type: 'text/csv;charset=utf-8' }),
            rows: slice.length,
        });
    }
    return parts;
}

/**
 * Data rows in a CSV — what the import will actually process.
 *
 * <p>Not `text.split('\n').length`: the title line and the header are not data, a
 * quoted field may contain a newline, and both exports are padded with blank lines
 * below the data. It reuses the same reading the splitter does, so the count and the
 * split can never disagree about what a row is.
 */
export async function countDataRows(file) {
    const records = splitRecords(stripBom(await file.text()));
    if (records.length === 0) {
        return 0;
    }
    return records
        .slice(findHeaderDepth(records) + 1)
        .filter((r) => r.trim() !== '')
        .length;
}

/**
 * Split into records, honouring quotes.
 *
 * A newline inside a quoted field belongs to that field, not to the file. Tracking the
 * quote state is the whole difference between this and `split('\n')`.
 */
export function splitRecords(text) {
    const records = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
        const char = text[i];

        if (char === '"') {
            // A doubled quote inside a quoted field is an escaped quote, not the end
            // of it. Consume both and stay where we are.
            if (inQuotes && text[i + 1] === '"') {
                current += '""';
                i++;
                continue;
            }
            inQuotes = !inQuotes;
            current += char;
            continue;
        }

        if (!inQuotes && (char === '\n' || char === '\r')) {
            // Treat CRLF as one break rather than two.
            if (char === '\r' && text[i + 1] === '\n') {
                i++;
            }
            records.push(current);
            current = '';
            continue;
        }

        current += char;
    }
    if (current !== '') {
        records.push(current);
    }
    return records;
}

/**
 * Index of the header record.
 *
 * The first record with more than one field — a title line holds exactly one. Falls
 * back to 0, which is correct for a plain export with no title line.
 */
export function findHeaderDepth(records) {
    for (let i = 0; i < Math.min(records.length, 10); i++) {
        if (countFields(records[i]) > 1) {
            return i;
        }
    }
    return 0;
}

/** Fields in one record, honouring quotes. */
function countFields(record) {
    let fields = 1;
    let inQuotes = false;
    for (let i = 0; i < record.length; i++) {
        if (record[i] === '"') {
            if (inQuotes && record[i + 1] === '"') {
                i++;
                continue;
            }
            inQuotes = !inQuotes;
        } else if (!inQuotes && record[i] === ',') {
            fields++;
        }
    }
    return fields;
}

/**
 * Excel writes a BOM when saving CSV as UTF-8; it is not part of the first header.
 *
 * Exported for the export detector, which reads the same first line and must see the
 * same first column — with the BOM left on, "Customer ID" is not "Customer ID".
 */
export function stripBom(text) {
    return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}
